import {
  BankStorage,
  BankDownloadData,
  BankIndexItem,
  StorageBackend,
} from '../storage/BankStorage';

/**
 * 下载进度信息
 */
export interface DownloadProgress {
  bankId: string;
  bankName: string;
  status: 'pending' | 'downloading' | 'completed' | 'failed' | 'cancelled';
  progress: number; // 0-100
  message?: string;
  error?: string;
}

/**
 * 题库更新信息
 */
export interface BankUpdate {
  bankId: string;
  bankName: string;
  currentVersion: string;
  latestVersion: string;
  hasUpdate: boolean;
}

/**
 * API 客户端接口
 */
export interface ApiClient {
  /** 获取题库列表（商店） */
  getBanks(): Promise<{
    banks: Array<{
      id: string;
      name: string;
      version: string;
      access?: { hasAccess: boolean };
    }>;
  }>;

  /** 下载题库数据 */
  downloadBank(
    bankId: string,
    deviceId?: string
  ): Promise<{ success: boolean; data: BankDownloadData }>;

  /** 检查题库更新 */
  checkBankUpdate(
    bankId: string,
    currentVersion: string
  ): Promise<{
    success: boolean;
    data: {
      hasUpdate: boolean;
      hasAccess: boolean;
      currentVersion: string | null;
      latestVersion: string;
    };
  }>;
  /** Optional delta endpoint used when a local package is stale. */
  syncBank?: (
    bankId: string,
    currentVersion: string
  ) => Promise<{
    success: boolean;
    data: BankDownloadData & {
      deletedQuestionIds?: string[];
      fullReplace?: boolean;
      unchanged?: boolean;
    };
  }>;
}

/**
 * 下载管理器配置
 */
export interface DownloadManagerConfig {
  /** API 客户端 */
  apiClient: ApiClient;
  /** 存储后端（可选） */
  storageBackend?: StorageBackend;
  /** 设备 ID（用于下载记录） */
  deviceId?: string;
}

type ProgressCallback = (progress: DownloadProgress) => void;

/**
 * 题库下载管理器
 *
 * 负责：
 * - 下载题库数据
 * - 管理下载进度
 * - 检查题库更新
 * - 取消下载
 */
export class DownloadManager {
  private storage: BankStorage;
  private apiClient: ApiClient;
  private deviceId?: string;

  /** 下载进度回调 */
  private progressCallbacks: Set<ProgressCallback> = new Set();

  /** 当前下载状态 */
  private downloadStates: Map<string, DownloadProgress> = new Map();

  /** 取消标记 */
  private cancelledBanks: Set<string> = new Set();
  private lastDeletedQuestionIds: string[] = [];

  constructor(config: DownloadManagerConfig) {
    this.apiClient = config.apiClient;
    this.storage = new BankStorage(config.storageBackend);
    this.deviceId = config.deviceId;
  }

  /**
   * 注册进度回调
   */
  onProgress(callback: ProgressCallback): () => void {
    this.progressCallbacks.add(callback);
    return () => {
      this.progressCallbacks.delete(callback);
    };
  }

  /**
   * 通知进度更新
   */
  private notifyProgress(progress: DownloadProgress): void {
    this.downloadStates.set(progress.bankId, progress);
    this.progressCallbacks.forEach((cb) => cb(progress));
  }

  /**
   * 下载题库
   * @param bankId 题库ID
   * @param bankName 题库名称（可选）
   * @param forceDownload 是否强制下载（跳过版本检查），默认 false
   */
  async downloadBank(
    bankId: string,
    bankName?: string,
    forceDownload: boolean = false
  ): Promise<boolean> {
    // 检查是否已取消
    if (this.cancelledBanks.has(bankId)) {
      this.cancelledBanks.delete(bankId);
      return false;
    }

    const name = bankName || bankId;

    // 如果强制下载，跳过版本检查，直接执行下载
    if (forceDownload) {
      console.log('[DownloadManager] Force download requested, skipping version check:', {
        bankId,
      });
    } else {
      // 检查是否已下载
      const isDownloaded = await this.storage.isDownloaded(bankId);
      if (isDownloaded) {
        // 重要：仅凭索引判断“已下载”不够，可能存在索引残留/下载中断/数据损坏
        // 必须确认本地题库数据真实可用（至少能读到题目数组且非空）
        const localBank = await this.storage.getBank(bankId);
        const hasUsableLocalData =
          !!localBank && Array.isArray(localBank.questions) && localBank.questions.length > 0;

        if (!hasUsableLocalData) {
          console.warn(
            '[DownloadManager] Local index indicates downloaded, but data is missing/empty. Forcing re-download:',
            { bankId }
          );
          // 不直接 return，让逻辑继续走真实下载接口 /api/banks/:id/download
        } else {
          // 获取本地版本
          const localVersion = await this.storage.getLocalVersion(bankId);

          if (localVersion) {
            // 获取服务器版本（需要先获取题库列表）
            try {
              const serverBanks = await this.apiClient.getBanks();
              const serverBank = serverBanks.banks.find((b) => b.id === bankId);

              if (serverBank && localVersion === serverBank.version) {
                // 版本匹配，不需要下载，直接返回成功
                this.notifyProgress({
                  bankId,
                  bankName: name,
                  status: 'completed',
                  progress: 100,
                  message: '已使用本地缓存',
                });
                return true;
              }
            } catch (error) {
              // 获取服务器版本失败，继续执行下载（可能是网络问题）
              console.warn('获取服务器版本失败，继续下载:', error);
            }
          }
        }
      }
    }

    // 开始下载
    this.notifyProgress({
      bankId,
      bankName: name,
      status: 'downloading',
      progress: 0,
      message: '正在连接服务器...',
    });

    try {
      // 模拟进度更新（实际下载是单次请求）
      this.notifyProgress({
        bankId,
        bankName: name,
        status: 'downloading',
        progress: 20,
        message: '正在下载题库数据...',
      });

      // 检查是否取消
      if (this.cancelledBanks.has(bankId)) {
        this.cancelledBanks.delete(bankId);
        this.notifyProgress({
          bankId,
          bankName: name,
          status: 'cancelled',
          progress: 0,
          message: '下载已取消',
        });
        return false;
      }

      // 调用 API 下载
      const result = await this.apiClient.downloadBank(bankId, this.deviceId);

      if (!result.success) {
        throw new Error('下载失败');
      }

      // 更新进度
      this.notifyProgress({
        bankId,
        bankName: name,
        status: 'downloading',
        progress: 60,
        message: '正在保存到本地...',
      });

      // 检查是否取消
      if (this.cancelledBanks.has(bankId)) {
        this.cancelledBanks.delete(bankId);
        this.notifyProgress({
          bankId,
          bankName: name,
          status: 'cancelled',
          progress: 0,
          message: '下载已取消',
        });
        return false;
      }

      // 保存到本地存储
      await this.storage.saveBank(result.data);

      // 完成
      this.notifyProgress({
        bankId,
        bankName: name,
        status: 'completed',
        progress: 100,
        message: '下载完成',
      });

      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '未知错误';

      this.notifyProgress({
        bankId,
        bankName: name,
        status: 'failed',
        progress: 0,
        message: '下载失败',
        error: errorMessage,
      });

      return false;
    }
  }

  /**
   * Ensure a named server revision is usable locally. Supplying expectedVersion
   * deliberately avoids the catalogue request: callers already have the
   * authoritative version from their accessible-inventory response.
   */
  async ensureBank(bankId: string, bankName: string, expectedVersion?: string): Promise<boolean> {
    this.lastDeletedQuestionIds = [];
    const localBank = await this.storage.getBank(bankId);
    if (
      expectedVersion &&
      localBank?.meta.version === expectedVersion &&
      localBank.questions.length > 0
    ) {
      this.notifyProgress({
        bankId,
        bankName,
        status: 'completed',
        progress: 100,
        message: '已使用本地缓存',
      });
      return true;
    }

    if (expectedVersion && localBank?.meta.version && this.apiClient.syncBank) {
      try {
        const result = await this.apiClient.syncBank(bankId, localBank.meta.version);
        if (result.success && result.data.unchanged) {
          if ((localBank.questions?.length ?? 0) > 0) {
            this.notifyProgress({
              bankId,
              bankName,
              status: 'completed',
              progress: 100,
              message: '已使用本地缓存',
            });
            return true;
          }
          // Version matches but local package empty/corrupt — fall through to full download.
        } else if (result.success && !result.data.fullReplace) {
          const deletedIds = result.data.deletedQuestionIds ?? [];
          await this.storage.mergeBankDelta(bankId, {
            latestVersion: result.data.bank.version,
            added: result.data.questions,
            deletedIds,
            bank: {
              name: result.data.bank.name,
              examType: result.data.bank.examType,
              section: result.data.bank.section,
            },
          });
          this.lastDeletedQuestionIds = deletedIds;
          this.notifyProgress({
            bankId,
            bankName,
            status: 'completed',
            progress: 100,
            message: '增量同步完成',
          });
          return true;
        } else if (result.success && result.data.fullReplace) {
          await this.storage.saveBank(result.data);
          this.lastDeletedQuestionIds = result.data.deletedQuestionIds ?? [];
          this.notifyProgress({
            bankId,
            bankName,
            status: 'completed',
            progress: 100,
            message: '同步完成',
          });
          return true;
        }
      } catch (error) {
        console.warn('[DownloadManager] 增量同步失败，改为完整下载:', error);
      }
    }

    // Caller already knows the authoritative version — force download, skip catalogue probe.
    return this.downloadBank(bankId, bankName, Boolean(expectedVersion));
  }

  getLastDeletedQuestionIds(): string[] {
    return [...this.lastDeletedQuestionIds];
  }

  /**
   * 批量下载题库
   */
  async downloadBanks(banks: Array<{ id: string; name: string }>): Promise<Map<string, boolean>> {
    const results = new Map<string, boolean>();

    for (const bank of banks) {
      const success = await this.downloadBank(bank.id, bank.name);
      results.set(bank.id, success);
    }

    return results;
  }

  /**
   * 取消下载
   */
  cancelDownload(bankId: string): void {
    this.cancelledBanks.add(bankId);
  }

  /**
   * 检查单个题库更新
   */
  async checkBankUpdate(bankId: string): Promise<BankUpdate | null> {
    const localVersion = await this.storage.getLocalVersion(bankId);
    if (!localVersion) {
      return null;
    }

    const meta = await this.storage.getBankMeta(bankId);
    if (!meta) {
      return null;
    }

    // 验证题库数据是否真的存在（不仅要有 meta，还要有实际的题目数据）
    // 如果数据不存在或为空，说明题库已被删除或损坏，不应该检查更新
    const bankData = await this.storage.getBank(bankId);
    if (!bankData || !bankData.questions || bankData.questions.length === 0) {
      // 数据不存在或为空，不检查更新
      console.warn(`[DownloadManager] 题库数据不存在或为空，跳过更新检查: ${bankId}`);
      return null;
    }

    try {
      const result = await this.apiClient.checkBankUpdate(bankId, localVersion);
      return {
        bankId,
        bankName: meta.name,
        currentVersion: localVersion,
        latestVersion: result.data.latestVersion,
        hasUpdate: result.data.hasUpdate,
      };
    } catch (error: any) {
      // 如果题库在服务器上不存在（404），清理本地索引
      if (error?.statusCode === 404 || error?.code === 'BANK_NOT_FOUND') {
        console.warn(`[DownloadManager] 题库在服务器上不存在，清理本地索引: ${bankId}`);
        try {
          await this.storage.deleteBank(bankId);
        } catch (deleteError) {
          console.error(`[DownloadManager] 清理本地索引失败: ${bankId}`, deleteError);
        }
      }
      return null;
    }
  }

  /**
   * 检查所有已下载题库的更新
   */
  async checkAllUpdates(): Promise<BankUpdate[]> {
    const downloadedBanks = await this.storage.getDownloadedBanks();
    const updates: BankUpdate[] = [];

    for (const bank of downloadedBanks) {
      const update = await this.checkBankUpdate(bank.id);
      if (update && update.hasUpdate) {
        updates.push(update);
      }
    }

    return updates;
  }

  /**
   * 更新题库（重新下载）
   */
  async updateBank(bankId: string): Promise<boolean> {
    const meta = await this.storage.getBankMeta(bankId);
    // 更新语义：强制重新下载并覆盖本地缓存
    return this.downloadBank(bankId, meta?.name, true);
  }

  /**
   * 获取已下载的题库列表
   */
  async getDownloadedBanks(): Promise<BankIndexItem[]> {
    return this.storage.getDownloadedBanks();
  }

  /**
   * 检查题库是否已下载
   */
  async isDownloaded(bankId: string): Promise<boolean> {
    return this.storage.isDownloaded(bankId);
  }

  /**
   * 删除本地题库
   */
  async deleteBank(bankId: string): Promise<void> {
    return this.storage.deleteBank(bankId);
  }

  /**
   * 获取当前下载状态
   */
  getDownloadState(bankId: string): DownloadProgress | undefined {
    return this.downloadStates.get(bankId);
  }

  /**
   * 获取所有下载状态
   */
  getAllDownloadStates(): Map<string, DownloadProgress> {
    return new Map(this.downloadStates);
  }

  /**
   * 获取存储实例（用于直接访问本地数据）
   */
  getStorage(): BankStorage {
    return this.storage;
  }
}
